/* PALISADE CUSTOM — <palisade-buy-box-gallery> custom element.
   Moved verbatim out of blocks/_palisade-buy-box-media.liquid so the class is
   fetched once from the CDN (cached a year) instead of being inlined per block
   instance — the homepage renders the block twice and shipped ~45 KB of this
   in every HTML response. Loaded with `defer` from the block; guarded by
   customElements.get so a second tag is a no-op. Do not add Liquid here. */
  (function () {
    if (customElements.get('palisade-buy-box-gallery')) return;

    /* PALISADE CUSTOM — thumb rail drives the stage on desktop; on mobile the
       stage is a scroll-snap carousel and tapping a thumb scrolls to the slide. */
    class PalisadeBuyBoxGallery extends HTMLElement {
      connectedCallback() {
        /* Listeners go on the HOST and resolve the thumb at event time — NOT on
           the thumbs themselves at connect time.

           When this block renders twice on one page (the homepage has two Buy
           Box sections), the second element is parsed AFTER the first block's
           script has already called customElements.define(). The parser
           therefore upgrades it and fires connectedCallback at its START TAG,
           before a single thumb has been parsed. Caching the children here left
           the second gallery with zero listeners — its thumbnail clicks did
           nothing — while the first gallery worked, because define() runs below
           its own markup and upgrades it with the subtree already intact.

           Delegation also survives the theme editor replacing the subtree. The
           `bound` guard stops a reorder (disconnect + reconnect with the subtree
           intact) from stacking a second handler set. */
        if (!this.bound) {
          this.bound = true;
          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('keydown', this.onKey.bind(this));
        }

        this.onResize = this.onViewportChange.bind(this);
        window.addEventListener('resize', this.onResize);

        /* ---- Playback state ------------------------------------------------
           Autoplay is a merchant setting, but reduced motion outranks it: a
           visitor who has asked the OS for less movement gets the poster and a
           play button instead. `paused` is the single source of truth for the
           whole gallery — pausing follows the visitor from slide to slide
           rather than resetting every time the stage changes. */
        this.paused =
          this.getAttribute('data-pbb-autoplay') === 'false' ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /* Nothing plays until the gallery is on screen — the homepage renders
           two of these, both below the fold. With no IntersectionObserver we
           fail OPEN: the slide still plays, it is just not viewport-gated. */
        this.inView = !('IntersectionObserver' in window);
        if ('IntersectionObserver' in window && !this.observer) {
          var gallery = this;
          this.observer = new IntersectionObserver(
            function (entries) {
              gallery.inView = entries[entries.length - 1].isIntersecting;
              gallery.syncPlayback();
            },
            { threshold: 0.2 }
          );
          this.observer.observe(this);
        }

        /* Engines disagree about whether muted autoplay pauses in a backgrounded
           tab, so stop it here rather than leave a clip decoding behind a tab the
           visitor walked away from. */
        this.onVisibility = this.syncPlayback.bind(this);
        document.addEventListener('visibilitychange', this.onVisibility);
        /* The subtree may not exist yet (see above), so defer the first sync
           instead of reading an empty gallery and giving up. Retried over a few
           frames rather than once: a single rAF was enough for syncMobileSlides
           (which fails soft — it just does nothing), but the loop needs the stage
           to exist or the carousel silently never becomes infinite. */
        var self = this;
        var tries = 0;
        (function init() {
          if (self.stage) {
            self.buildLoop();
            self.syncMobileSlides();
            self.wasMobile = self.isMobile;
            if (self.isMobile) self.centerOn(self.activeIndex(), 'instant');
            self.bindStageScroll();
            self.syncPlayback();
            return;
          }
          if (++tries < 6) requestAnimationFrame(init);
        })();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.settleTimer) clearTimeout(this.settleTimer);
        if (this.playTimer) clearTimeout(this.playTimer);
        /* A theme-editor reorder detaches the subtree intact — without this the
           clip would keep decoding in a gallery that is no longer on the page. */
        var self = this;
        this.cards.forEach(function (card) {
          self.stopCard(card);
        });
      }

      /* Queried per use, never cached — the subtree can arrive after
         connectedCallback, or be swapped out by the theme editor. */
      get thumbs() {
        return Array.prototype.slice.call(this.querySelectorAll('[data-pbb-thumb]'));
      }

      get stage() {
        return this.querySelector('[data-pbb-stage]');
      }

      /* Real slides only — clones deliberately carry no data-pbb-slide, so every
         index in this array still lines up 1:1 with the thumbs. */
      get slides() {
        var stage = this.stage;
        return stage ? Array.prototype.slice.call(stage.querySelectorAll('[data-pbb-slide]')) : [];
      }

      /* Everything on the rail in DOM order, clones included — the geometry the
         scroll position actually snaps against. */
      get cards() {
        var stage = this.stage;
        return stage
          ? Array.prototype.slice.call(stage.querySelectorAll('[data-pbb-slide], [data-pbb-clone]'))
          : [];
      }

      get isMobile() {
        return window.matchMedia('(max-width: 989px)').matches;
      }

      /* Index of the thumb an event came from, or -1 when the event started
         outside the rail (clicks land on the <img> inside the button). */
      thumbIndex(e) {
        if (!e.target || !e.target.closest) return -1;
        var thumb = e.target.closest('[data-pbb-thumb]');
        return thumb ? this.thumbs.indexOf(thumb) : -1;
      }

      onClick(e) {
        /* Tested before the rail: the toggle is a sibling of the stage, so
           thumbIndex() returns -1 for it and the click would be dropped. */
        if (e.target && e.target.closest && e.target.closest('[data-pbb-video-toggle]')) {
          this.paused = !this.paused;
          this.syncPlayback();
          return;
        }
        var i = this.thumbIndex(e);
        if (i < 0) return;
        /* PALISADE CUSTOM: a click is a commitment to ONE slide, so it does not
           pay the 180ms arrow-traversal guard — that delay was the first third
           of the measured wait before a clip appeared.

           Desktop only. On mobile presentingCard() resolves by geometry against
           a rail that centerOn() has only just started smooth-scrolling, so
           starting synchronously would play the clip the visitor is swiping AWAY
           from. Mobile keeps the default and is already served by the 120ms
           scroll-settle handler. */
        this.activate(i, this.isMobile ? undefined : 0);
        this.thumbs[i].focus();
      }

      onKey(e) {
        var i = this.thumbIndex(e);
        if (i < 0) return;
        var thumbs = this.thumbs;
        var last = thumbs.length - 1;
        var next;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
        if (next === undefined) return;
        e.preventDefault();
        this.activate(next);
        thumbs[next].focus();
      }

      /* State only — no scrolling. Split out of activate() because the scroll
         handler has to be able to record which slide the rail settled on without
         scrolling back to it, which would fight the user's own gesture. */
      markActive(index, delay) {
        var mobile = this.isMobile;
        this.thumbs.forEach(function (thumb, i) {
          var on = i === index;
          thumb.classList.toggle('is-active', on);
          /* aria-current (slide-picker buttons), not aria-selected: the rail is
             not a tablist — see the markup comment on the thumb rail. */
          if (on) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
          thumb.setAttribute('tabindex', on ? '0' : '-1');
        });
        this.slides.forEach(function (slide, i) {
          var on = i === index;
          slide.classList.toggle('is-active', on);
          slide.hidden = mobile ? false : !on; /* carousel shows every slide */
        });
        /* Every route into a new slide lands here — thumb click, arrow key, and
           the carousel settling — so playback follows the stage from one place
           instead of from each of them. `delay` is passed straight through so a
           caller that has already committed to a slide can skip the wait. */
        this.syncPlayback(delay);
      }

      activate(index, delay) {
        this.markActive(index, delay);
        if (!this.isMobile) return;
        /* Honour reduced motion (same pattern as palisade-scroll-to.js). */
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.centerOn(index, reduce ? 'instant' : 'smooth');
      }

      activeIndex() {
        var slides = this.slides;
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].classList.contains('is-active')) return i;
        }
        return 0;
      }

      /* ---- Infinite loop ----------------------------------------------------
         Figma 110:2066 shows THREE cards at all times: one centred with a 39px
         peek of its neighbours either side. A plain scroll-snap rail cannot do
         that at the ends — slide 1 has nothing to its left and the last slide
         nothing to its right, so the peek collapses to empty padding and the
         swipe dead-ends.

         Fixed by cloning the last card before the first and the first card after
         the last. The clones supply the missing peek, and because a clone is
         pixel-identical to the card it duplicates, the rail can be silently
         teleported from a clone to its real counterpart once the scroll settles —
         which is what makes the carousel continuous in both directions with no
         visible jump.

         Clones are inert: no id (duplicating one would break aria-controls), no
         data-pbb-slide (so slide indices stay 1:1 with the thumbs), no role or
         slide-position label to mis-announce, and aria-hidden so AT sees each
         image once. CSS hides them outside the mobile carousel. */
      buildLoop() {
        var stage = this.stage;
        if (!stage) return;
        var slides = this.slides;
        /* Nothing to loop through with a single image, and a re-entrant call
           (theme editor reorder keeps the subtree) must not stack more clones. */
        if (slides.length < 2 || stage.querySelector('[data-pbb-clone]')) return;
        var last = slides.length - 1;
        stage.insertBefore(this.cloneCard(slides[last], last), slides[0]);
        stage.appendChild(this.cloneCard(slides[0], 0));
      }

      cloneCard(slide, ofIndex) {
        var clone = slide.cloneNode(true);
        ['id', 'data-pbb-slide', 'role', 'aria-roledescription', 'aria-label', 'hidden'].forEach(
          function (attr) {
            clone.removeAttribute(attr);
          }
        );
        Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (node) {
          node.removeAttribute('id');
        });
        /* Moving media never survives cloning. A duplicated <video> would be a
           second stream of the same bytes for a card the visitor sees for the
           moment before the wrap teleports off it, and a duplicated embed would
           be a second YouTube player. The poster <img> stays, so the clone still
           looks like the card it stands in for. */
        Array.prototype.forEach.call(
          clone.querySelectorAll('[data-pbb-video], [data-pbb-embed]'),
          function (node) {
            node.parentNode.removeChild(node);
          }
        );
        /* Scroll-reveal stamps its state on elements it is watching. Copying
           that state into a clone nothing observes would leave the card stuck at
           opacity 0 — a blank gap in the carousel with no way back. */
        Array.prototype.forEach.call(clone.querySelectorAll('[data-reveal-state]'), function (node) {
          node.removeAttribute('data-reveal-state');
        });
        clone.removeAttribute('data-reveal-state');
        clone.classList.remove('is-active');
        clone.setAttribute('aria-hidden', 'true');
        /* aria-hidden alone would leave anything focusable inside a duplicate in
           the tab order while hidden from assistive tech. */
        clone.setAttribute('inert', '');
        clone.setAttribute('data-pbb-clone', String(ofIndex));
        return clone;
      }

      /* Scrolls the STAGE only. scrollIntoView would also scroll every ancestor,
         which on a page whose scroller is .page-wrapper meant centring a slide
         could jump the whole page. */
      centerOn(index, behavior) {
        var slide = this.slides[index];
        if (slide) this.scrollToCard(slide, behavior);
      }

      scrollToCard(card, behavior) {
        var stage = this.stage;
        if (!stage) return;
        var sr = stage.getBoundingClientRect();
        var cr = card.getBoundingClientRect();
        var left = stage.scrollLeft + (cr.left + cr.width / 2) - (sr.left + sr.width / 2);
        /* Direct assignment for the instant case: it cannot be overridden by an
           inherited scroll-behavior, which matters because the wrap must never be
           visible as a movement. */
        if (behavior === 'instant') stage.scrollLeft = left;
        else stage.scrollTo({ left: left, behavior: behavior });
      }

      bindStageScroll() {
        var stage = this.stage;
        if (!stage || stage.dataset.pbbScrollBound) return;
        stage.dataset.pbbScrollBound = '1';
        var self = this;
        /* Debounced rather than per-event: the wrap may only happen once the rail
           has come to rest, otherwise it would cut across iOS momentum. `scrollend`
           is not used because it is still missing in Safari, and the timer covers
           every engine identically. */
        stage.addEventListener(
          'scroll',
          function () {
            if (self.settleTimer) clearTimeout(self.settleTimer);
            self.settleTimer = setTimeout(function () {
              self.onScrollSettle();
            }, 120);
          },
          { passive: true }
        );
      }

      /* The card whose centre is closest to the stage's centre — i.e. the one
         scroll-snap has settled on. */
      nearestCard() {
        var stage = this.stage;
        if (!stage) return null;
        var sr = stage.getBoundingClientRect();
        var mid = sr.left + sr.width / 2;
        var best = null;
        var bestDistance = Infinity;
        this.cards.forEach(function (card) {
          var cr = card.getBoundingClientRect();
          var distance = Math.abs(cr.left + cr.width / 2 - mid);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = card;
          }
        });
        return best;
      }

      onScrollSettle() {
        if (!this.isMobile) return;
        var card = this.nearestCard();
        if (!card) return;
        var cloneOf = card.getAttribute('data-pbb-clone');
        if (cloneOf === null) {
          this.markActive(this.slides.indexOf(card));
          return;
        }
        /* Landed on a clone: jump to the identical real card. The rail is already
           at a snap point and the target is another one, so re-snapping after the
           assignment is a no-op. */
        var index = Number(cloneOf);
        this.centerOn(index, 'instant');
        this.markActive(index);
      }

      /* On mobile every slide must be visible for the swipe carousel. */
      syncMobileSlides() {
        var slides = this.slides;
        if (!slides.length) return;
        var mobile = this.isMobile;
        slides.forEach(function (s) {
          s.hidden = mobile ? false : !s.classList.contains('is-active');
        });
      }

      /* ---- Playback ---------------------------------------------------------
         One clip plays at a time, and only the one the visitor is looking at.
         Everything else is paused and — for embeds — torn down completely.

         Sources are mounted here rather than in the markup. That is the whole
         reason this code exists: the homepage renders this gallery twice, each
         with every slide in the DOM, and on desktop all but one slide is
         display:none. A src in the HTML means every browser fetches every clip
         in all of them; a src assigned on demand means it fetches exactly one. */

      shouldPlay() {
        return !!this.inView && !this.paused && !document.hidden;
      }

      /* The card whose media is entitled to play: the active slide on desktop,
         and on mobile whatever the carousel is actually sitting on — which may
         be a clone mid-wrap, hence cards rather than slides. */
      presentingCard() {
        if (this.isMobile) {
          return this.nearestCard() || this.slides[this.activeIndex()] || null;
        }
        return this.slides[this.activeIndex()] || null;
      }

      syncPlayback(delay) {
        var self = this;
        var target = this.shouldPlay() ? this.presentingCard() : null;

        /* Stopping is immediate — nothing should keep playing off screen. */
        this.cards.forEach(function (card) {
          if (card !== target) self.stopCard(card);
        });
        this.syncToggle();

        if (this.playTimer) clearTimeout(this.playTimer);
        if (!target) return;
        /* Starting waits. Arrowing along the thumb rail walks through every
           slide on the way to the one the visitor wants; without this, each
           keystroke would kick off a fetch for a clip they are already past.
           PALISADE CUSTOM: callers that have already committed to one slide
           (a thumb click on desktop) pass 0 and start immediately. */
        var wait = typeof delay === 'number' ? delay : 180;
        if (wait <= 0) {
          this.startCard(target);
          return;
        }
        this.playTimer = setTimeout(function () {
          self.startCard(target);
        }, wait);
      }

      startCard(card) {
        if (!card) return;
        var video = card.querySelector('[data-pbb-video]');
        if (video) {
          this.mountVideo(video);
          var playing = video.play();
          /* play() rejects when an engine declines autoplay despite the mute —
             surface that as the paused state rather than a control that lies
             about what the clip is doing.

             AbortError is the exception, and the common one: it only means a
             pause() overtook a play() that had not resolved yet, which is what
             swiping quickly through the carousel looks like. Treating that as a
             refusal would let an ordinary swipe pause the whole gallery. */
          if (playing && playing.catch) {
            var self = this;
            playing.catch(function (error) {
              if (error && error.name === 'AbortError') return;
              self.paused = true;
              self.syncToggle();
            });
          }
          return;
        }
        var embed = card.querySelector('[data-pbb-embed]');
        if (embed) this.mountEmbed(embed);
      }

      stopCard(card) {
        if (!card) return;
        var video = card.querySelector('[data-pbb-video]');
        if (video && !video.paused) video.pause();
        var embed = card.querySelector('[data-pbb-embed]');
        if (embed) this.unmountEmbed(embed);
      }

      mountVideo(video) {
        if (video.getAttribute('data-pbb-mounted') !== null) return;
        var sources = video.querySelectorAll('source[data-pbb-src]');
        if (!sources.length) return;
        Array.prototype.forEach.call(sources, function (source) {
          source.setAttribute('src', source.getAttribute('data-pbb-src'));
        });
        video.setAttribute('data-pbb-mounted', '');
        /* PALISADE CUSTOM: the markup ships preload="none" so nothing is fetched
           for a slide nobody has opened. Once a clip IS wanted, that hint only
           starves it — the 5s loop re-buffered at its own wrap point. Lifted to
           'auto' at mount, which is scoped to the one slide being activated. */
        video.preload = 'auto';
        /* The attribute alone is not enough on iOS, which reads the property at
           the moment play() is called. Without both, autoplay is refused. */
        video.muted = true;
        /* Revealed only once a frame exists: an empty <video> paints as a black
           box in some engines, and the poster underneath is the better thing to
           be looking at until then. */
        video.addEventListener(
          'loadeddata',
          function () {
            video.setAttribute('data-pbb-ready', '');
          },
          { once: true }
        );
        video.load();
      }

      /* Embeds are mounted and unmounted rather than played and paused: without
         the host's JS API there is no way to pause a YouTube or Vimeo iframe, and
         removing it stops the clip for certain and reclaims the player. */
      mountEmbed(embed) {
        if (embed.querySelector('iframe')) return;
        var src = embed.getAttribute('data-pbb-embed-src');
        if (!src) return;
        var frame = document.createElement('iframe');
        frame.setAttribute('src', src);
        frame.setAttribute('title', embed.getAttribute('data-pbb-embed-title') || '');
        frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('loading', 'lazy');
        embed.appendChild(frame);
        embed.setAttribute('data-pbb-mounted', '');
      }

      unmountEmbed(embed) {
        var frame = embed.querySelector('iframe');
        if (frame) frame.parentNode.removeChild(frame);
        embed.removeAttribute('data-pbb-mounted');
      }

      syncToggle() {
        var toggle = this.querySelector('[data-pbb-video-toggle]');
        if (!toggle) return;
        var card = this.presentingCard();
        var moving = !!(card && card.querySelector('[data-pbb-video], [data-pbb-embed]'));
        /* Hidden over a still: a play button that controls nothing on screen is
           worse than no button at all. */
        toggle.hidden = !moving;
        toggle.classList.toggle('is-paused', this.paused);
        toggle.setAttribute(
          'aria-label',
          this.paused
            ? toggle.getAttribute('data-pbb-label-play')
            : toggle.getAttribute('data-pbb-label-pause')
        );
      }

      onViewportChange() {
        var mobile = this.isMobile;
        this.syncMobileSlides();
        /* Crossing into the carousel, the stage becomes a scroller at scrollLeft
           0 — which centres the leading CLONE, not slide 1. Re-centre on whatever
           is active so the gallery never opens on a duplicate. */
        if (mobile && !this.wasMobile) this.centerOn(this.activeIndex(), 'instant');
        this.wasMobile = mobile;
        /* Crossing the breakpoint changes which card counts as "on screen" —
           the active slide on desktop, the centred card on mobile. */
        this.syncPlayback();
      }
    }

    customElements.define('palisade-buy-box-gallery', PalisadeBuyBoxGallery);
  })();
